import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DollarSign, Eye, Check, X } from 'lucide-react';
import { useToast } from '../../components/ui/use-toast';
import { api } from '../../services/api';

const AdminAffiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadAffiliates();
  }, []);

  const loadAffiliates = async () => {
    try {
      const data = await api.getAffiliates();
      setAffiliates(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load affiliates',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (affiliateId, newStatus) => {
    try {
      await api.updateAffiliate(affiliateId, { status: newStatus });
      toast({
        title: 'Success',
        description: `Affiliate ${newStatus === 'active' ? 'approved' : 'updated'}`
      });
      loadAffiliates();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update affiliate',
        variant: 'destructive'
      });
    }
  };

  const handlePayout = async (e) => {
    e.preventDefault();
    
    try {
      await api.createAffiliatePayout(selectedAffiliate._id, {
        affiliate_id: selectedAffiliate._id,
        amount: parseFloat(payoutAmount),
        payment_method: selectedAffiliate.payment_method || 'Manual',
        notes: payoutNotes
      });
      
      toast({
        title: 'Success',
        description: 'Payout processed successfully'
      });
      
      setShowPayoutModal(false);
      setPayoutAmount('');
      setPayoutNotes('');
      setSelectedAffiliate(null);
      loadAffiliates();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to process payout',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Affiliate Management</h1>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-sm text-gray-600">Total Affiliates</p>
          <p className="text-2xl font-bold">{affiliates.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-600">Active</p>
          <p className="text-2xl font-bold">
            {affiliates.filter(a => a.status === 'active').length}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-600">Pending</p>
          <p className="text-2xl font-bold">
            {affiliates.filter(a => a.status === 'pending').length}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-600">Total Pending Commission</p>
          <p className="text-2xl font-bold">
            ${affiliates.reduce((sum, a) => sum + (a.pending_commission || 0), 0).toFixed(2)}
          </p>
        </Card>
      </div>

      {/* Affiliates List */}
      <div className="grid gap-4">
        {affiliates.length === 0 ? (
          <Card className="p-6 text-center text-gray-500">
            No affiliates yet
          </Card>
        ) : (
          affiliates.map((affiliate) => (
            <Card key={affiliate._id} className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{affiliate.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusBadge(affiliate.status)}`}>
                      {affiliate.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{affiliate.email}</p>
                  <p className="text-sm font-mono text-gray-800 mt-1">
                    Code: <span className="font-bold">{affiliate.affiliate_code}</span>
                  </p>
                </div>

                <div className="flex gap-2">
                  {affiliate.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleStatusUpdate(affiliate._id, 'active')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusUpdate(affiliate._id, 'suspended')}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  {affiliate.status === 'active' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusUpdate(affiliate._id, 'suspended')}
                    >
                      Suspend
                    </Button>
                  )}
                  {affiliate.status === 'suspended' && (
                    <Button
                      size="sm"
                      onClick={() => handleStatusUpdate(affiliate._id, 'active')}
                    >
                      Activate
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-5 gap-4 text-sm border-t pt-4">
                <div>
                  <p className="text-gray-600">Commission Rate</p>
                  <p className="font-semibold">{affiliate.commission_rate}%</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Referrals</p>
                  <p className="font-semibold">{affiliate.total_referrals || 0}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Sales</p>
                  <p className="font-semibold">${(affiliate.total_sales || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Pending Commission</p>
                  <p className="font-semibold text-orange-600">
                    ${(affiliate.pending_commission || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Paid Commission</p>
                  <p className="font-semibold text-green-600">
                    ${(affiliate.paid_commission || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {affiliate.pending_commission > 0 && affiliate.status === 'active' && (
                <div className="mt-4 pt-4 border-t">
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedAffiliate(affiliate);
                      setPayoutAmount(affiliate.pending_commission.toString());
                      setShowPayoutModal(true);
                    }}
                  >
                    <DollarSign className="w-4 h-4 mr-1" />
                    Process Payout
                  </Button>
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">Process Payout</h2>
            <form onSubmit={handlePayout} className="space-y-4">
              <div>
                <Label>Affiliate</Label>
                <p className="text-sm text-gray-600">{selectedAffiliate?.name}</p>
                <p className="text-xs text-gray-500">{selectedAffiliate?.email}</p>
              </div>

              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  required
                />
              </div>

              <div>
                <Label>Payment Method</Label>
                <p className="text-sm text-gray-600">
                  {selectedAffiliate?.payment_method || 'Manual'}
                </p>
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <textarea
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2"
                  rows="3"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Process Payout</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPayoutModal(false);
                    setSelectedAffiliate(null);
                    setPayoutAmount('');
                    setPayoutNotes('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminAffiliates;
